import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import TableSection from '../../components/tables/TableSection';
import FilterDropdown from '../../components/filters/FilterDropdown';
import styles from '../../styles/subscriptions.module.scss';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import { formatBooleanAsPill } from '../../utils/formatBoolean';
import {
  fetchAwardEventSummary,
  exportAwardEventSummary,
} from './award-event-api';
import Spinner from '../../components/elements/Spinner/Spinner';

const USER_TYPE_LABELS = {
  regular: 'Regularan korisnik',
  vip: 'VIP',
  subscriber: 'Pretplatnik',
  vip_subscriber: 'VIP + Pretplatnik',
};

const userTypeSections = [
  {
    type: 'userType',
    title: 'Tip korisnika',
    items: [
      { key: null, label: 'Svi' },
      ...Object.entries(USER_TYPE_LABELS).map(([key, label]) => ({
        key,
        label,
      })),
    ],
  },
];

const columns = [
  { key: 'fullName', label: 'Korisnik' },
  { key: 'email', label: 'Email' },
  { key: 'phoneNumber', label: 'Telefon' },
  {
    key: 'summaryOfOrderPricesPaid',
    label: 'Ukupno potrošeno',
    render: (val) => formatPriceWithCurrency(val),
  },
  { key: 'tokens', label: 'Tokeni' },
  {
    key: 'surveyCompleted',
    label: 'Anketa popunjena',
    render: (val) => formatBooleanAsPill(val),
  },
];

const NagradaPage = () => {
  const [userType, setUserType] = useState(null);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notActiveMessage, setNotActiveMessage] = useState(null);

  const loadData = useCallback(async () => {
    const { data, success, notActive, message } = await fetchAwardEventSummary({
      page: currentPage,
      limit: 10,
      userType,
    });

    if (success) {
      setNotActiveMessage(null);
      setItems(data.items);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
    } else if (notActive) {
      setNotActiveMessage(message || 'Nagrada trenutno nije aktivna.');
    } else {
      toast.error(message || 'Greška pri učitavanju podataka.');
    }
    setLoading(false);
  }, [currentPage, userType]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [currentPage, userType]);

  const handleFilterChange = (filter) => {
    setUserType(filter.userType || null);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    const { success, message } = await exportAwardEventSummary({ userType });
    setIsExporting(false);

    if (success) {
      toast.success('Podaci uspešno izvezeni.');
    } else {
      toast.error(message || 'Greška pri izvozu podataka.');
    }
  };

  return (
    <ContentLayout header="Nagrada 2026">
      <div className={styles.wrapper}>
        {notActiveMessage ? (
          <div
            style={{ padding: '48px', textAlign: 'center', color: '#667085' }}
          >
            <p>{notActiveMessage}</p>
          </div>
        ) : (
          <>
            <div className={styles.filters}>
              <div
                className={styles.filters__left}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FilterDropdown
                  sections={userTypeSections}
                  selectedFilter={{ userType }}
                  onFilterChange={handleFilterChange}
                  buttonLabel={
                    userType ? USER_TYPE_LABELS[userType] : 'Tip korisnika'
                  }
                />
                <Tooltip
                  title={
                    <span>
                      Filter prikazuje <b>trenutni</b> status korisnika (VIP /
                      pretplata). Tokeni se računaju na osnovu statusa korisnika
                      u trenutku svake pojedinačne porudžbine, pa se mogu
                      razlikovati od trenutnog statusa.
                    </span>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#6D758F' }} />
                </Tooltip>
              </div>

              <Button
                className="btn-tertiary"
                onClick={handleExport}
                loading={isExporting}
              >
                Izvezi CSV
              </Button>
            </div>

            {loading ? (
              <Spinner size="lg" />
            ) : (
              <TableSection
                columns={columns}
                actions={[]}
                data={items}
                emptyState={{
                  icon: '/assets/icons/no-users.svg',
                  title: 'Nema podataka za prikazivanje',
                }}
                showPagination={items.length > 0}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onSearch={() => {}}
                searchPlaceholder=""
              />
            )}
          </>
        )}
      </div>
    </ContentLayout>
  );
};

export default NagradaPage;
